import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { CalendarDays, Clock, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { updateSEOTags, seoConfigs } from "@/utils/seo";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage: string;
  imageAlt: string;
  category: string;
  tags: string[];
  readTime: number;
  viewCount: number;
  publishedAt: string;
  author: {
    firstName: string;
    lastName: string;
  };
}

interface BlogResponse {
  posts: BlogPost[];
  total: number;
}

export default function Blog() {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const { data, isLoading, error } = useQuery<BlogResponse>({
    queryKey: ['/api/blog', currentPage],
    queryFn: async () => {
      const response = await fetch(`/api/blog?page=${currentPage}&limit=${postsPerPage}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      return response.json();
    },
  });

  // SEO Optimization
  useEffect(() => {
    updateSEOTags(seoConfigs.blog);
  }, []);

  const totalPages = data ? Math.ceil(data.total / postsPerPage) : 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-6">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog</h1>
          <p className="text-red-600">Failed to load blog posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="blog-heading">
            Prize Drop Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-testid="blog-description">
            Expert tips, competition guides, and winning strategies to help you succeed in every competition
          </p>
        </div>

        {/* Blog Posts Grid */}
        {data?.posts.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">No blog posts yet</h2>
            <p className="text-gray-600">Check back soon for expert tips and competition guides!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {data?.posts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300" data-testid={`blog-post-${post.id}`}>
                  <Link href={`/blog/${post.slug}`}>
                    <div className="cursor-pointer">
                      {post.featuredImage && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={post.featuredImage}
                            alt={post.imageAlt || post.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            data-testid={`blog-image-${post.id}`}
                            onError={(e) => {
                              console.error('Image failed to load:', post.featuredImage);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <CardHeader className="space-y-3">
                        {post.category && (
                          <Badge variant="secondary" className="w-fit" data-testid={`blog-category-${post.id}`}>
                            {post.category}
                          </Badge>
                        )}
                        
                        <h2 className="text-xl font-semibold text-gray-900 leading-tight hover:text-blue-600 transition-colors" data-testid={`blog-title-${post.id}`}>
                          {post.title}
                        </h2>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <p className="text-gray-600 line-clamp-3" data-testid={`blog-excerpt-${post.id}`}>
                          {post.excerpt}
                        </p>

                        {/* Meta information */}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <CalendarDays className="h-4 w-4" />
                              <span data-testid={`blog-date-${post.id}`}>{formatDate(post.publishedAt)}</span>
                            </div>
                            
                            {post.readTime && (
                              <div className="flex items-center space-x-1">
                                <Clock className="h-4 w-4" />
                                <span data-testid={`blog-read-time-${post.id}`}>{post.readTime} min read</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-1">
                            <Eye className="h-4 w-4" />
                            <span data-testid={`blog-views-${post.id}`}>{post.viewCount || 0}</span>
                          </div>
                        </div>

                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {post.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs" data-testid={`blog-tag-${post.id}-${index}`}>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </div>
                  </Link>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  data-testid="blog-prev-page"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>

                <div className="flex items-center space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      data-testid={`blog-page-${page}`}
                    >
                      {page}
                    </Button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  data-testid="blog-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* SEO Content Section */}
        <div className="mt-16 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Your Guide to Competition Success
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Stay ahead with our comprehensive competition guides, expert tips, and winning strategies. 
              From understanding entry requirements to maximizing your chances of success, our blog 
              covers everything you need to know about competitions, giveaways, and prizes. Join 
              thousands of successful participants who trust Prize Drop for the latest insights and 
              proven techniques to win amazing prizes.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  );
}